import { Controller } from "@hotwired/stimulus"

// Shared behaviour contract for the HAML filter / date cards (#13753) — the
// server-rendered twin of the React FilterDropdown / DateRangeDropdown
// staged-edit model:
//
//   * the trigger button toggles a titled panel (role=dialog) that takes
//     initial focus itself, never a control inside it;
//   * every edit inside the panel is a DRAFT held in the DOM; nothing is
//     submitted until Done (the subclass's `commit`);
//   * Cancel, the header X, Escape and an outside click DISCARD the draft
//     (the subclass's `restoreDraft` puts the committed state back) so the next
//     open shows exactly what is applied;
//   * a nested menu (the multi-select popover) consumes its own Escape first
//     — the keydown listener runs in the CAPTURE phase so it sees the popover
//     still open and steps aside.
//
// Subclasses declare `static targets` including "wrapper", "button" and
// "panel", and implement `readCommitted()` (snapshot at connect),
// `restoreDraft()` (DOM ← snapshot) and `commit()` (submit).
export default class FilterCardDisclosureController extends Controller {
  static NESTED_OPEN_MENU = ".gl-multi-select-popover.open"

  initialize() {
    this.handleDocClick = this.handleDocClick.bind(this)
    this.handleKey = this.handleKey.bind(this)
    this.handleBeforeCache = this.handleBeforeCache.bind(this)
  }

  connect() {
    this.committed = this.readCommitted()
    document.addEventListener("click", this.handleDocClick)
    document.addEventListener("keydown", this.handleKey, true)
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)
  }

  disconnect() {
    document.removeEventListener("click", this.handleDocClick)
    document.removeEventListener("keydown", this.handleKey, true)
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
  }

  // Turbo snapshots the live DOM for its cache right before it navigates
  // away. A card left open with a DRAFT in it would come back open on Back,
  // and the reconnect would read that draft as the committed state
  // (`readCommitted` runs in connect). Discard the draft and close, exactly
  // as Cancel does, so the cached page is the applied, closed page (#14692).
  handleBeforeCache() {
    if (!this.isOpen()) return
    this.restoreDraft()
    this.close()
  }

  // ── Subclass contract (defaults are inert so a bare panel still toggles) ──
  readCommitted() { return null }
  restoreDraft() {}
  commit() {}

  toggle(event) {
    event?.preventDefault()
    this.isOpen() ? this.cancel() : this.open()
  }

  open() {
    if (!this.hasPanelTarget) return
    this.restoreDraft()
    this.panelTarget.classList.add("open")
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.add("active")
      this.buttonTarget.setAttribute("aria-expanded", "true")
    }
    // The dialog itself takes focus (APG non-modal dialog default); the header
    // Close is a tooltip host and would paint its bubble over the title.
    this.panelTarget.focus({ preventScroll: true })
  }

  // Cancel / header X: discard the draft, close, return focus to the trigger.
  cancel(event) {
    event?.preventDefault()
    this.restoreDraft()
    this.close()
    if (this.hasButtonTarget) this.buttonTarget.focus({ preventScroll: true })
  }

  // Done: the ONLY commit point. `commit` submits when the draft differs from
  // the committed state; an unchanged Done just closes (no reload) and hands
  // focus back to the trigger — otherwise focus would sit on the now-hidden
  // Done button and the next Tab would start from nowhere (A11Y-2). A changed
  // Done navigates, so no focus move is forced ahead of the submit.
  done(event) {
    event?.preventDefault()
    if (this.commit() === true) return
    this.close()
    if (this.hasButtonTarget) this.buttonTarget.focus({ preventScroll: true })
  }

  // A trigger with data-sticky-active="true" keeps its `active` paint while
  // closed (the React date trigger stays lit while a range is applied).
  close() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.remove("open")
    if (this.hasButtonTarget) {
      if (this.buttonTarget.dataset.stickyActive !== "true") this.buttonTarget.classList.remove("active")
      this.buttonTarget.setAttribute("aria-expanded", "false")
    }
  }

  isOpen() {
    return this.hasPanelTarget && this.panelTarget.classList.contains("open")
  }

  form() {
    return this.element.closest("form")
  }

  // Outside clicks discard the draft and close; focus stays where the click
  // landed (it may be another card's trigger).
  handleDocClick(event) {
    if (!this.isOpen() || !this.hasWrapperTarget) return
    if (this.wrapperTarget.contains(event.target)) return
    this.restoreDraft()
    this.close()
  }

  handleKey(event) {
    if (event.key !== "Escape" || !this.isOpen()) return
    if (this.hasPanelTarget && this.panelTarget.querySelector(this.constructor.NESTED_OPEN_MENU)) return
    event.preventDefault()
    this.cancel()
  }
}
